import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Target, Loader2, ChevronLeft } from "lucide-react";
import { useExecutiveActions } from "../lib/queries";

/**
 * ExecutiveActionsInline — replaces the "click button → modal → wait" flow
 * with an always-visible "3 things you'd do this week" panel right on the home page.
 *
 * This IS the demo's wow moment. Don't hide it behind a button.
 */

const PRIORITY_TONE = {
  1: {
    label: "P1 · השבוע",
    chip: "bg-red-500/15 text-red-300 border-red-500/30",
    bar: "bg-red-500/60",
    number: "text-red-300",
  },
  2: {
    label: "P2 · השבוע",
    chip: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    bar: "bg-amber-500/60",
    number: "text-amber-300",
  },
  3: {
    label: "P3 · בקרוב",
    chip: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
    bar: "bg-cyan-500/60",
    number: "text-cyan-300",
  },
};

export default function ExecutiveActionsInline() {
  // React Query: 5 min stale, cached across page navigations. The LLM call
  // is expensive (~15s) — without caching the user would wait every time
  // they bounce home → inventory → home. Now it's instant after first load.
  const { data, isLoading, error } = useExecutiveActions();
  const loading = isLoading;
  const actions = data?.actions || null;
  const errorMsg = error?.response?.data?.detail || (error ? "שגיאה בטעינת התוכנית" : null);

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-6"
      data-testid="exec-actions-inline"
    >
      {/* Header band */}
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" strokeWidth={2} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-mono">
              Pulse AI · יועץ עסקי אישי
            </div>
            <h2 className="text-base sm:text-lg font-medium text-foreground leading-snug mt-0.5">
              השבוע, אם הייתי מנהל את העסק שלך —
            </h2>
          </div>
        </div>
        {!loading && actions && (
          <div className="text-[11px] text-zinc-500 font-mono">
            3 פעולות · ממויינות לפי דחיפות × ₪
          </div>
        )}
      </div>

      {loading && <LoadingState />}

      {errorMsg && !loading && (
        <div className="bg-card border border-red-500/20 rounded-md p-4 text-sm text-red-300">
          {errorMsg}
        </div>
      )}

      {!loading && actions && actions.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {actions.slice(0, 3).map((a, i) => (
            <ActionCard key={i} action={a} index={i} />
          ))}
        </div>
      )}
    </motion.section>
  );
}

function ActionCard({ action, index }) {
  const priority = action.priority || index + 1;
  const tone = PRIORITY_TONE[priority] || PRIORITY_TONE[3];

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.1 + index * 0.08 }}
      className="relative bg-card border border-border rounded-md p-4 overflow-hidden hover:border-primary/30 transition-colors group"
      data-testid={`exec-action-inline-${index}`}
    >
      {/* Side bar */}
      <div className={`absolute right-0 top-0 bottom-0 w-1 ${tone.bar}`} />

      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2.5 pr-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-mono border ${tone.chip}`}>
            {tone.label}
          </span>
          {action.owner_hint && (
            <span className="text-[10px] text-zinc-500 bg-zinc-800/50 px-1.5 py-0.5 rounded">
              {action.owner_hint}
            </span>
          )}
        </div>
        <div className={`text-3xl font-light leading-none ${tone.number} opacity-50 group-hover:opacity-100 transition-opacity`}>
          {priority}
        </div>
      </div>

      {/* Title */}
      <h3 className="text-sm font-semibold text-foreground leading-snug mb-2.5 pr-2">
        {action.title}
      </h3>

      {/* Why */}
      {action.why && (
        <p className="text-[12px] text-zinc-400 leading-relaxed mb-2.5 pr-2 line-clamp-3">
          {action.why}
        </p>
      )}

      {/* Impact */}
      {action.impact && (
        <div className={`text-[12px] ${tone.number} leading-snug pr-2 flex items-start gap-1`}>
          <Target className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={2} />
          <span><span className="text-zinc-500 font-medium">השפעה: </span>{action.impact}</span>
        </div>
      )}
    </motion.div>
  );
}

const LOADING_STAGES = [
  "סורק את הדובדבנים שזוהו...",
  "מצליב עם המגמות החודשיות...",
  "בונה תוכנית פעולה לשבוע...",
];

function LoadingState() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % LOADING_STAGES.length), 2500);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="exec-actions-inline-loading">
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-card border border-border rounded-md p-4 h-40 flex items-center justify-center">
          {i === 1 ? (
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-primary animate-spin mx-auto mb-2" strokeWidth={1.5} />
              <div className="text-xs text-zinc-400">{LOADING_STAGES[idx]}</div>
            </div>
          ) : (
            <div className="w-full h-full animate-pulse rounded bg-zinc-800/30" />
          )}
        </div>
      ))}
    </div>
  );
}
